<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  summary: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['linkCandidates']);
const { t } = useI18n();

const formatNumber = value =>
  new Intl.NumberFormat().format(Number(value || 0));
const sourceLabels = computed(() => props.summary?.source_systems || []);

const stats = computed(() => {
  const summary = props.summary || {};
  return [
    {
      key: 'total',
      icon: 'i-lucide-users-round',
      label: t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.STATS.TOTAL'),
      value: formatNumber(summary.total_count),
    },
    {
      key: 'reachable',
      icon: 'i-lucide-send',
      label: t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.STATS.REACHABLE'),
      value: formatNumber(summary.reachable_count),
    },
    {
      key: 'resume',
      icon: 'i-lucide-file-user',
      label: t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.STATS.RESUME'),
      value: formatNumber(summary.with_resume_count),
    },
    {
      key: 'sources',
      icon: 'i-lucide-radio-tower',
      label: t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.STATS.SOURCES'),
      value: formatNumber(sourceLabels.value.length),
    },
  ];
});

const hasCandidates = computed(
  () => Number(props.summary?.total_count || 0) > 0
);
const updatedAtLabel = computed(() => {
  const timestamp = props.summary?.last_source_updated_at;
  if (!timestamp) return t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.UPDATED_EMPTY');

  return new Date(timestamp * 1000).toLocaleString();
});
</script>

<template>
  <CardLayout>
    <div class="relative flex flex-col gap-5 overflow-hidden rounded-3xl">
      <div
        class="absolute right-0 top-0 size-40 rounded-full bg-n-blue-5 opacity-20 blur-3xl"
      />
      <div
        class="absolute -bottom-10 left-10 size-32 rounded-full bg-n-teal-5 opacity-20 blur-3xl"
      />

      <div
        class="relative flex flex-col gap-5 rounded-3xl border border-n-weak bg-gradient-to-br from-n-alpha-2 to-n-background p-5 lg:flex-row lg:items-start lg:justify-between"
      >
        <div class="flex min-w-0 flex-col gap-4">
          <div
            class="flex w-fit items-center gap-2 rounded-full border border-n-weak bg-n-alpha-2 px-3 py-1 text-xs font-medium uppercase tracking-[0.14em] text-n-brand"
          >
            <span class="i-lucide-briefcase-business size-4" />
            {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.EYEBROW') }}
          </div>
          <div class="flex flex-col gap-2">
            <h3 class="max-w-3xl text-2xl font-semibold text-n-slate-12">
              {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.TITLE') }}
            </h3>
            <p class="max-w-3xl text-sm text-n-slate-11">
              {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.DESCRIPTION') }}
            </p>
          </div>

          <div
            class="flex flex-wrap items-center gap-3 text-xs text-n-slate-10"
          >
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-sparkles size-4 text-n-brand" />
              {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.SOCIAL_PROOF') }}
            </span>
            <span class="hidden size-1 rounded-full bg-n-slate-6 sm:block" />
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-refresh-cw size-4" />
              {{
                t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.UPDATED_AT', {
                  date: updatedAtLabel,
                })
              }}
            </span>
          </div>
        </div>

        <div class="flex shrink-0 flex-col gap-4 lg:items-end">
          <div class="flex -space-x-3">
            <span
              class="flex size-11 items-center justify-center rounded-full border-2 border-n-background bg-n-blue-4 text-n-blue-11"
            >
              <span class="i-lucide-user-round size-5" />
            </span>
            <span
              class="flex size-11 items-center justify-center rounded-full border-2 border-n-background bg-n-teal-4 text-n-teal-11"
            >
              <span class="i-lucide-graduation-cap size-5" />
            </span>
            <span
              class="flex size-11 items-center justify-center rounded-full border-2 border-n-background bg-n-amber-4 text-n-amber-11"
            >
              <span class="i-lucide-badge-check size-5" />
            </span>
          </div>
          <Button
            icon="i-lucide-user-round-plus"
            :label="t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.ACTION')"
            :disabled="!hasCandidates || isLoading"
            @click="emit('linkCandidates')"
          />
        </div>
      </div>

      <div
        v-if="isLoading"
        class="flex items-center justify-center py-8 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <div class="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <div
            v-for="stat in stats"
            :key="stat.key"
            class="group flex items-center gap-3 rounded-2xl border border-n-weak bg-n-alpha-1 px-4 py-3 transition-all hover:-translate-y-0.5 hover:bg-n-alpha-2"
          >
            <span
              class="flex size-10 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-brand"
            >
              <span :class="stat.icon" class="size-5" />
            </span>
            <span class="flex min-w-0 flex-col">
              <span class="text-xl font-semibold text-n-slate-12">
                {{ stat.value }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ stat.label }}
              </span>
            </span>
          </div>
        </div>

        <div
          class="flex flex-col gap-2 rounded-2xl border border-n-weak bg-n-alpha-1 p-4"
        >
          <span
            class="text-xs font-medium uppercase tracking-[0.12em] text-n-slate-10"
          >
            {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.SOURCES') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="source in sourceLabels"
              :key="source"
              class="flex items-center gap-1 rounded-full bg-n-blue-3 px-3 py-1 text-xs font-medium text-n-blue-11"
            >
              <span class="i-lucide-circle-dot size-3" />
              {{ source }}
            </span>
            <span v-if="!sourceLabels.length" class="text-xs text-n-slate-10">
              {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.SOURCES_EMPTY') }}
            </span>
          </div>
        </div>

        <div
          class="rounded-2xl border border-n-weak bg-n-alpha-1 px-4 py-3 text-sm text-n-slate-11"
        >
          <div class="flex items-start gap-3">
            <span class="i-lucide-shield-check size-5 shrink-0 text-n-brand" />
            <div class="flex flex-col gap-1">
              <span class="font-medium text-n-slate-12">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.SAFETY_TITLE') }}
              </span>
              <span>
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_BASE.SAFETY_BODY') }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </CardLayout>
</template>
